import { logWarn } from '@/lib/observability/logger'

const IS_PROD = process.env.NODE_ENV === 'production'
const LOCAL_FALLBACK_SITE_URL = 'http://localhost:3000'
const PRODUCTION_FALLBACK_SITE_URL = 'https://example.com'
const FALLBACK_SITE_URL = IS_PROD ? PRODUCTION_FALLBACK_SITE_URL : LOCAL_FALLBACK_SITE_URL

const configuredSiteUrl =
  process.env.NEXT_PUBLIC_SITE_URL ||
  process.env.SITE_URL ||
  (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : '') ||
  ''

const rawSiteUrl = configuredSiteUrl || FALLBACK_SITE_URL

function normalizeSiteUrl(value: string): string {
  const trimmed = value.trim()
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(withProtocol)
    url.pathname = '/'
    url.search = ''
    url.hash = ''
    return url.toString().replace(/\/$/, '')
  } catch {
    return FALLBACK_SITE_URL
  }
}

function normalizeExternalUrl(value?: string): string | null {
  const trimmed = (value || '').trim()
  if (!trimmed) return null

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(withProtocol)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url.toString()
  } catch {
    return null
  }
}

export const SITE_URL = normalizeSiteUrl(rawSiteUrl)
export const SITE_NAME = 'Цифровой горизонт'
export const IS_FALLBACK_SITE_URL = SITE_URL === FALLBACK_SITE_URL || !configuredSiteUrl

// Contact constants (single source of truth)
export const COMPANY_PHONE = '+7 (905) 470-46-76'
export const COMPANY_PHONE_RAW = '+79054704676'
export const COMPANY_EMAIL = 'nosikov2014@list.ru'
export const COMPANY_ADDRESS = {
  street: 'ул. Степная',
  city: 'Майкоп',
  region: 'Республика Адыгея',
  postalCode: '385000',
  country: 'RU',
}
export const COMPANY_GEO = { lat: 44.6078, lng: 40.1058 }

// The office's actual pin on Yandex Maps — also used as the "Построить
// маршрут" link on /contacts (see ContactsLocation.tsx), so it's a real,
// verifiable profile rather than a placeholder.
export const COMPANY_YANDEX_MAPS_URL = 'https://yandex.ru/maps/-/CDuNB-3S'

export const COMPANY_SOCIALS = {
  instagram: normalizeExternalUrl(process.env.NEXT_PUBLIC_INSTAGRAM_URL),
  telegram: normalizeExternalUrl(process.env.NEXT_PUBLIC_TELEGRAM_URL),
  vk: normalizeExternalUrl(process.env.NEXT_PUBLIC_VK_URL),
  // Claim these listings (Яндекс Бизнес, 2ГИС) and set the env vars once
  // they exist — an unclaimed/unverified business profile shouldn't be
  // linked from sameAs, so these are opt-in via env rather than guessed URLs.
  yandexBusiness: normalizeExternalUrl(process.env.NEXT_PUBLIC_YANDEX_BUSINESS_URL),
  twoGis: normalizeExternalUrl(process.env.NEXT_PUBLIC_2GIS_URL),
  yandexMaps: normalizeExternalUrl(COMPANY_YANDEX_MAPS_URL),
}

export const COMPANY_SOCIAL_LINKS = Object.values(COMPANY_SOCIALS).filter((link): link is string =>
  Boolean(link),
)

declare global {
  var __seoSiteUrlWarned: boolean | undefined
}

function shouldWarnFallbackSiteUrl(): boolean {
  if (typeof window !== 'undefined') return false
  if (!IS_FALLBACK_SITE_URL) return false
  if (!IS_PROD) return true
  return process.env.SEO_WARN_FALLBACK_SITE_URL !== '0'
}

if (shouldWarnFallbackSiteUrl() && !globalThis.__seoSiteUrlWarned) {
  globalThis.__seoSiteUrlWarned = true
  logWarn('seo.site_url_fallback_active', {
    message:
      'SITE_URL uses fallback value. Set NEXT_PUBLIC_SITE_URL (or SITE_URL) for production canonical URLs.',
  })
}

export function getMetadataBase(): URL {
  return new URL(`${SITE_URL}/`)
}

export function absoluteUrl(path = '/'): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return new URL(normalizedPath, `${SITE_URL}/`).toString()
}
